import { Observable, Subject, zip } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TranslateService } from '@ngx-translate/core';
import { InspectionType, InspectionTypeItem } from '../../../_objects/InspectionType';
import { SubjectLocation, SubjectModel, SubjectModelClone, SubjectModelNew, SubjectModelUpdate } from '../../../_objects/subject-model';
import { UtilsService } from '../../../_services/_utils.service';
import { CommonSnackBarService } from '../../../_services/common-snack-bar.service';
import { InspectionTypeService } from '../../../_services/http-api/inspection-type.service';
import { SubjectModelService } from '../../../_services/http-api/subject-model.service';
import { LangUpdateService } from '../../../_services/lang-update.service';
import { SpinnerService } from '../../../_services/spinner.service';
import { CustomValidators } from '../../../_shared/custom-validator';
import { REGULAR_EXPRESSION } from '../../../app.constants';
import { CommonErrorMessageDialogComponent } from '../../../common-dialog/common-error-message-dialog.component';

@Component({
    selector: 'app-subject-model-edit-dialog',
    templateUrl: './subject-model-edit-dialog.component.html',
    styleUrls: ['./subject-model-edit-dialog.component.scss'],
    standalone: false
})
export class SubjectModelEditDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  nameMaxLength = 64;
  regularExpression = this.utilsService.CombiningRegExp([
    '^',
    REGULAR_EXPRESSION.RESERVED_WORDS_PATTERN,
    '[',
    REGULAR_EXPRESSION.HALF_ALPHANUMERIC_PATTERN,
    REGULAR_EXPRESSION.KANJI_PATTERN,
    REGULAR_EXPRESSION.SYMBOL_PATTERN,
    ']+$'
  ]);
  nameControl = new FormControl<string>(null, [
    CustomValidators.requiredTrimmed(),
    Validators.maxLength(this.nameMaxLength),
    Validators.pattern(this.regularExpression)
  ]);
  inspectionTypeControl = new FormControl<InspectionTypeItem>({ value: null, disabled: false }, [CustomValidators.requiredTrimmed()]);
  subjectModel: SubjectModel;
  uploadImagePath: string;
  uploadFile: File;
  newFlag = true;
  cloneFlag = false;
  isTrans = false;
  inspectionType: InspectionType;
  private onDestroy$ = new Subject();

  get toggleText(): string {
    return this.toggle && this.toggle.checked
      ? this.translate.instant('subjectModelEditDialog.enabled')
      : this.translate.instant('subjectModelEditDialog.disabled');
  }

  get isSubmitInValid() {
    return this.isTrans
      || this.nameControl.invalid
      || (!this.cloneFlag && this.inspectionTypeControl.invalid);
  }

  get inspectionTypeItems(): InspectionTypeItem[] {
    return this.inspectionType?.items.filter(item => item.isValid);
  }

  @ViewChild(MatSlideToggle) toggle: MatSlideToggle;
  constructor(
    public dialogRef: MatDialogRef<SubjectModelEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private snackBar: CommonSnackBarService,
    private dialog: MatDialog,
    private messageDialog: MatDialog,
    private subjectModelService: SubjectModelService,
    private cd: ChangeDetectorRef,
    private oscSpinnerService: SpinnerService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private inspectionTypeService: InspectionTypeService,
    private utilsService: UtilsService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
  }

  ngOnInit() {
    this.newFlag = this.data.flag === 'new';
    this.cloneFlag = this.data.flag === 'clone';
  }

  ngAfterViewInit() {
    this.getInitData();
  }
  getInitData() {
    this.oscSpinnerService.show(true);

    const obs: Observable<any>[] = [this.inspectionTypeService.getInspectionTypes()];
    if (!this.newFlag) {
      // 更新の場合、SubjectModelも取得
      obs.push(this.subjectModelService.getSubjectModel(this.data.modelId));
    }

    zip(...obs).subscribe(
      res => {
        this.inspectionType = res[0];
        this.setNewModelValue();
        if (!this.newFlag) {
          this.subjectModel = res[1];

          if (!this.cloneFlag) {
            this.nameControl.setValue(this.subjectModel.name);
            const targetType = this.inspectionTypeItems.find(item => item.key === this.subjectModel.inspectionType);
            this.inspectionTypeControl.setValue(targetType);
            this.toggle.checked = this.subjectModel.isValid;
          }
        }

        this.oscSpinnerService.hide(true);
        this.cd.detectChanges();
      },
      error => {
        console.log(error);
        this.snackBar.open(this.translate.instant('subjectModelEditDialog.setEditModelValueError'));
        this.oscSpinnerService.hide(true);
        this.cd.detectChanges();
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  setNewModelValue() {
    this.toggle.checked = false; // 初期値:false
  }

  getDefaultLocations(): SubjectLocation[] {
    const location: SubjectLocation = new SubjectLocation();
    location.identifier = 'DataPlate';
    location.name = 'DataPlate';
    location.abbreviation = 'DataPlate';
    location.description = '';
    location.inspectionFor = [];
    location.isDataPlate = true;
    location.rectInStructImage = {
      'top': 0,
      'left': 0,
      'width': 0,
      'height': 0
    };
    location.children = [];
    return [location];
  }
  submit() {
    this.oscSpinnerService.show(true);
    this.isTrans = true;
    if (this.newFlag) {
      const modelNew: SubjectModelNew = new SubjectModelNew();
      modelNew.name = this.nameControl.value.trim();
      modelNew.locations = this.getDefaultLocations();
      modelNew.schemaVersion = '1';
      modelNew.isValid = this.toggle.checked;
      modelNew.inspectionType = this.inspectionTypeControl.value?.key;
      this.subjectModelService.addSubjectModel(modelNew).subscribe(
        res => {
          this.subjectModel = res;
        },
        (error: HttpErrorResponse) => {
          this.oscSpinnerService.hide(true);
          this.isTrans = false;
          if (error.status === 409) {
            this.openErrorMessageDialog();
          } else {
            this.snackBar.open(this.translate.instant('subjectModelEditDialog.submitError'));
            console.log(error);
          }
        },
        () => {
          this.oscSpinnerService.hide(true);
          this.isTrans = false;
          this.snackBar.open(this.translate.instant('subjectModelEditDialog.submitCompleted'));
          this.dialogRef.close(this.subjectModel.id);
        }
      );
    }
    else if (!this.cloneFlag) {
      const editModel: SubjectModelUpdate = new SubjectModelUpdate();
      editModel.name = this.nameControl.value.trim();
      editModel.locations = this.subjectModel.locations;
      editModel.schemaVersion = this.subjectModel.schemaVersion;
      editModel.id = this.subjectModel.id;
      editModel.isValid = this.toggle.checked;
      editModel.inspectionType = this.inspectionTypeControl.value?.key;
      this.subjectModelService.updateSubjectModel(editModel).subscribe(
        res => {
          this.subjectModel = res;
        },
        (error: HttpErrorResponse) => {
          this.oscSpinnerService.hide(true);
          this.isTrans = false;
          if (error.status === 409) {
            this.openErrorMessageDialog();
          } else {
            this.snackBar.open(this.translate.instant('subjectModelEditDialog.submitError'));
            console.log(error);
          }
        },
        () => {
          this.oscSpinnerService.hide(true);
          this.isTrans = false;
          this.snackBar.open(this.translate.instant('subjectModelEditDialog.submitCompleted'));
          this.dialogRef.close(this.subjectModel.id);
        }
      );
    }
    else {
      const cloneModel: SubjectModelClone = new SubjectModelClone();
      cloneModel.name = this.nameControl.value.trim();
      cloneModel.id = this.subjectModel.id;
      this.subjectModelService.cloneSubjectModel(cloneModel).subscribe(
        res => {
          this.subjectModel = res;
        },
        (error: HttpErrorResponse) => {
          this.oscSpinnerService.hide(true);
          this.isTrans = false;
          if (error.status === 409) {
            this.openErrorMessageDialog();
          } else {
            this.snackBar.open(this.translate.instant('subjectModelEditDialog.submitError'));
            console.log(error);
          }
        },
        () => {
          this.oscSpinnerService.hide(true);
          this.isTrans = false;
          this.snackBar.open(this.translate.instant('subjectModelEditDialog.submitCompleted'));
          this.dialogRef.close(this.subjectModel.id);
        }
      );
    }
  }
  openErrorMessageDialog(): void {
    this.messageDialog.open(CommonErrorMessageDialogComponent, {
      width: '480px',
      height: '400px',
      data: {
        firstMessage: this.translate.instant('subjectModelEditDialog.openErrorMessageDialogFirstMessage'),
        secondMessage: this.translate.instant('subjectModelEditDialog.openErrorMessageDialogSecondMessage'),
      }
    });
  }

}
